import {
  faCheck,
  faPen,
  faPlus,
  faTimes,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import { KeyboardEvent, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Modal } from 'src/common/components/Modal';
import { Tooltip } from 'src/common/components/Tooltip';
import { ActionIcon } from 'src/common/components/buttons/ActionIcon';
import { Button } from 'src/common/components/buttons/Button';
import { TextInput } from 'src/common/components/inputs/TextInput/TextInput';
import { updateTechCrew } from 'src/project/api/techCrewsApi';
import { TechCrew, WorkerEntry } from 'src/project/types/techCrews';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  techCrew: TechCrew;
  canEdit: boolean;
};

export const WorkersListModal = ({
  isOpen,
  onClose,
  techCrew,
  canEdit,
}: Props) => {
  const { t } = useTranslation(['project', 'common']);
  const { projectId = '' } = useParams();
  const queryClient = useQueryClient();

  const [workers, setWorkers] = useState<WorkerEntry[]>(techCrew.workers ?? []);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [newWorkerName, setNewWorkerName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Re-sync local state when the modal opens or the techCrew prop changes
  useEffect(() => {
    if (isOpen) {
      setWorkers(techCrew.workers ?? []);
      setEditingIndex(null);
      setEditingName('');
      setNewWorkerName('');
    }
  }, [isOpen, techCrew.workers]);

  const handleStartEdit = (index: number) => {
    setEditingIndex(index);
    setEditingName(workers[index].name);
  };

  const handleCommitEdit = () => {
    if (editingIndex === null) return;
    const trimmed = editingName.trim();
    if (trimmed) {
      const updated = [...workers];
      updated[editingIndex] = { name: trimmed };
      setWorkers(updated);
    }
    setEditingIndex(null);
    setEditingName('');
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditingName('');
  };

  const handleEditKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleCommitEdit();
    if (e.key === 'Escape') handleCancelEdit();
  };

  const handleRemove = (index: number) => {
    if (editingIndex === index) {
      setEditingIndex(null);
      setEditingName('');
    }
    setWorkers((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddWorker = () => {
    const trimmed = newWorkerName.trim();
    if (!trimmed) return;
    setWorkers((prev) => [...prev, { name: trimmed }]);
    setNewWorkerName('');
  };

  const handleAddKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleAddWorker();
  };

  const handleSave = async () => {
    // Commit any in-progress edit before saving
    let finalWorkers = workers;
    if (editingIndex !== null && editingName.trim()) {
      const updated = [...workers];
      updated[editingIndex] = { name: editingName.trim() };
      finalWorkers = updated;
    }

    setIsSaving(true);
    try {
      await updateTechCrew(projectId, techCrew._id, {
        workers: finalWorkers,
      });
      await queryClient.invalidateQueries({
        queryKey: ['projects', projectId],
      });
      toast.success(
        t('project:techCrewEdit.workersView.listModal.saveSuccess'),
      );
      onClose();
    } catch {
      toast.error(t('project:techCrewEdit.workersView.listModal.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setEditingIndex(null);
    setEditingName('');
    setNewWorkerName('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={handleClose} className="w-[520px]">
      <Modal.Header onClose={handleClose}>
        {t('project:techCrewEdit.workersView.listModal.title')}
      </Modal.Header>
      <Modal.Body>
        {workers.length === 0 && !canEdit ? (
          <p className="text-center text-shuttleGray-600">
            {t('project:techCrewEdit.workersView.listModal.emptyList')}
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 rounded border border-gray-200">
            {workers.map((worker, index) => (
              <li key={index} className="flex items-center gap-2 px-3 py-2">
                {canEdit && editingIndex === index ? (
                  <>
                    <TextInput
                      className="flex-1"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={handleEditKeyDown}
                      autoFocus
                      maxLength={255}
                    />
                    <Tooltip content={t('common:button.save')}>
                      <ActionIcon onClick={handleCommitEdit}>
                        <FontAwesomeIcon icon={faCheck} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip content={t('common:button.abort')}>
                      <ActionIcon onClick={handleCancelEdit}>
                        <FontAwesomeIcon icon={faTimes} />
                      </ActionIcon>
                    </Tooltip>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm">{worker.name}</span>
                    {canEdit && (
                      <>
                        <Tooltip content={t('common:button.edit')}>
                          <ActionIcon onClick={() => handleStartEdit(index)}>
                            <FontAwesomeIcon icon={faPen} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip content={t('common:button.delete')}>
                          <ActionIcon onClick={() => handleRemove(index)}>
                            <FontAwesomeIcon icon={faTrash} />
                          </ActionIcon>
                        </Tooltip>
                      </>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}

        {canEdit && (
          <div className="mt-4 flex gap-2">
            <TextInput
              className="flex-1"
              value={newWorkerName}
              onChange={(e) => setNewWorkerName(e.target.value)}
              onKeyDown={handleAddKeyDown}
              placeholder={t(
                'project:techCrewEdit.workersView.listModal.addPlaceholder',
              )}
              maxLength={255}
            />
            <Button
              variant="secondary"
              onClick={handleAddWorker}
              disabled={!newWorkerName.trim()}
            >
              <FontAwesomeIcon icon={faPlus} />
              {t('common:button.add')}
            </Button>
          </div>
        )}

        {canEdit && (
          <div className="mt-6 flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={isSaving}
            >
              {t('common:button.abort')}
            </Button>
            <Button onClick={handleSave} loading={isSaving} disabled={isSaving}>
              {t('common:button.save')}
            </Button>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};
